import React, { useEffect, useState } from 'react';

// @@NOTIFY_PICK_v115_4@@ @@NOTIFY_OPTIONS_v115_7@@ "Notify client" dialog: who gets the new-report email, whether it lists upcoming
// shows, an optional note, and a server-rendered PREVIEW of the exact email. Presentational; ClientDetail owns send + preview.
// Contacts with an activated portal login start ticked; contacts without an email can't be ticked. Closing is blocked while sending.
const NOTE_MAX = 2000;
const hasEmail = (c) => !!(c && c.email && String(c.email).trim());
const nameOf = (c) => (`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unnamed contact');
const accessOf = (c) => (c.portal_active ? 'Portal active' : c.portal_invited ? 'Invite pending' : 'No portal access');
const wrapDoc = (html) => '<!doctype html><html><body style="margin:16px;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1a1a1a;background:#ffffff">' + html + '</body></html>';

export default function NotifyClientModal({ clientName, label, notifiedAt, notifiedCount, contacts, showsSoon, busy, error, onSend, onPreview, onClose }) {
  const list = contacts || [];
  const [picked, setPicked] = useState(() => {
    const o = {}; list.forEach((c) => { if (hasEmail(c) && c.portal_active) o[c.id] = true; }); return o;
  });
  const [includeShows, setIncludeShows] = useState(showsSoon > 0);
  const [note, setNote] = useState('');
  const [previewOn, setPreviewOn] = useState(false);
  const [pv, setPv] = useState(null);       // { subject, html, from, reply_to, sample_name } | { error }
  const [pvBusy, setPvBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const ids = list.filter((c) => hasEmail(c) && picked[c.id]).map((c) => c.id);
  const noLogin = list.filter((c) => hasEmail(c) && picked[c.id] && !c.portal_active).length;
  const showsOn = includeShows && showsSoon > 0;
  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const setAll = (on) => setPicked(() => { const o = {}; if (on) list.forEach((c) => { if (hasEmail(c)) o[c.id] = true; }); return o; });

  // Preview: re-render (debounced) whenever recipients / shows toggle / note change while it is open.
  const previewKey = JSON.stringify([ids, showsOn, note]);
  useEffect(() => {
    if (!previewOn) return undefined;
    let dead = false;
    setPvBusy(true);
    const t = setTimeout(async () => {
      try { const d = await onPreview({ ids, includeShows: showsOn, note }); if (!dead) setPv(d); }
      catch (e) { if (!dead) setPv({ error: e.message || 'Could not build the preview.' }); }
      finally { if (!dead) setPvBusy(false); }
    }, 350);
    return () => { dead = true; clearTimeout(t); };
  }, [previewOn, previewKey]);

  return (
    <div className="hq-modal-backdrop" role="dialog" aria-modal="true" aria-label="Notify client"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="hq-modal" style={{ maxWidth: 720 }}>
        <div className="hq-modal-head">
          <h2>Notify {clientName || 'client'} — {label}</h2>
          <button type="button" className="hq-modal-x" aria-label="Close" disabled={busy} onClick={onClose}>×</button>
        </div>
        <div className="hq-modal-body">
          {notifiedAt && (
            <p className="hq-modal-hint" style={{ marginTop: 0 }}>
              Already sent {new Date(notifiedAt).toLocaleDateString()}{notifiedCount > 1 ? ` (${notifiedCount}×)` : ''}. Sending again emails whoever you tick below.
            </p>
          )}

          <span className="hq-modal-label">Send to</span>
          {list.length === 0 ? (
            <p className="hq-cd-muted">No contacts are attached to this client yet. Add one on the Contacts tab first.</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
                <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => setAll(true)}>All</button>
                <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => setAll(false)}>None</button>
              </div>
              {list.map((c) => {
                const ok = hasEmail(c);
                return (
                  <label key={c.id} className="hq-cd-check" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', opacity: ok ? 1 : 0.5 }}>
                    <input type="checkbox" disabled={!ok || busy} checked={!!picked[c.id] && ok} onChange={() => toggle(c.id)} />
                    <span style={{ flex: 1 }}>
                      <b>{nameOf(c)}</b>
                      <span className="hq-cd-muted"> · {ok ? c.email : 'no email address'}</span>
                    </span>
                    <span className="hq-cd-small hq-cd-muted">{accessOf(c)}</span>
                  </label>
                );
              })}
              {noLogin > 0 && (
                <p className="hq-modal-hint">{noLogin} selected {noLogin === 1 ? 'person has' : 'people have'} no portal login — their email says so and asks them to reply to be set up.</p>
              )}
            </>
          )}

          <span className="hq-modal-label">Upcoming dates</span>
          <label className="hq-cd-check" style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: showsSoon > 0 ? 1 : 0.55 }}>
            <input type="checkbox" disabled={showsSoon === 0 || busy} checked={showsOn} onChange={(e) => setIncludeShows(e.target.checked)} />
            <span>
              Include upcoming shows and ask them to reach out about tour merch
              <span className="hq-cd-muted"> · {showsSoon > 0 ? `${showsSoon} in the next 45 days` : 'no shows in the next 45 days'}{/* @@SHOW_DAYS_v115_8@@ */}</span>
            </span>
          </label>

          <span className="hq-modal-label">Add a note (optional)</span>
          <textarea className="hq-input" rows={4} style={{ width: '100%', resize: 'vertical' }} maxLength={NOTE_MAX} disabled={busy}
            placeholder="Shown near the top of the email, e.g. “Great month — call me if you want to talk about the fall run.”"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="hq-modal-hint">{note.length}/{NOTE_MAX} · plain text · replies go to info@xyzmerch.com</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
            <button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" disabled={busy} onClick={() => setPreviewOn((v) => !v)}>
              {previewOn ? 'Hide preview' : 'Preview email'}
            </button>
            {previewOn && pvBusy && <span className="hq-cd-small hq-cd-muted">Updating…</span>}
          </div>
          {previewOn && pv && pv.error && <p style={{ color: '#ff7a73', margin: '10px 0 0' }}>{pv.error}</p>}
          {previewOn && pv && !pv.error && (
            <div style={{ marginTop: 10 }}>
              <div className="hq-cd-small hq-cd-muted" style={{ marginBottom: 6, lineHeight: 1.6 }}>
                <div><b>From:</b> {pv.from}</div>
                <div><b>Reply-To:</b> {pv.reply_to}</div>
                <div><b>Subject:</b> {pv.subject}</div>
                <div>{pv.sample_name ? `Previewing as ${pv.sample_name}` : 'Previewing with a generic greeting'}{pv.shows > 0 ? ` · lists ${pv.shows} show${pv.shows === 1 ? '' : 's'}` : ' · no shows section'}</div>
              </div>
              <iframe title="Email preview" sandbox="" srcDoc={wrapDoc(pv.html)}
                style={{ width: '100%', height: 420, border: '1px solid var(--hq-line)', borderRadius: 8, background: '#ffffff', opacity: pvBusy ? 0.6 : 1 }} />
            </div>
          )}
          {error && <p style={{ color: '#ff7a73', margin: '10px 0 0' }}>{error}</p>}
        </div>
        <div className="hq-modal-foot">
          <button type="button" className="hq-btn hq-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="hq-btn" disabled={busy || ids.length === 0}
            onClick={() => onSend({ ids, includeShows: showsOn, note })}>
            {busy ? 'Sending…' : `Send to ${ids.length} ${ids.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
